import { useMemo } from "react";
import { TissueTypes } from "../../../../models";
import { TextFit } from "../../../../component-library";
import { getDonorIsbtNumber } from "../../../../helper-functions";
import { TIFFormDataProps, useTIFFormData } from "../../../../hooks";

export function TIFHeader({ tissueId }: Readonly<TIFFormDataProps>) {
    const {
        tissue,
        _case,
        donor,
        referral,
        productCode,
        latestStorageSolution,
        latestProcedure,
        tissueNumWithProcedure,
    } = useTIFFormData({ tissueId });

    const renderTerritoryLabel = useMemo(() => {
        const territoryLabel = _case?.referral?.territory?.label;
        return territoryLabel ? `| ${territoryLabel} Tissue` : "";
    }, [_case?.referral?.territory?.label]);

    const showFinP = useMemo(() => {
        return (
            !!latestProcedure?.office &&
            !!latestProcedure.office.territory &&
            (tissue?.isImported || latestProcedure.office.territoryId !== referral?.territoryId)
        );
    }, [latestProcedure, referral, tissue?.isImported]);

    const tissueSide = useMemo(() => {
        return tissue?.side === "OD" ? "Right" : "Left";
    }, [tissue?.side]);

    const tissueTypeName = useMemo(() => {
        switch (tissue?.tissueType?.name) {
            case TissueTypes.Dmek: {
                return "| DMEK";
            }
            case TissueTypes.Precut:
            case TissueTypes.Precut1:
            case TissueTypes.Precut2: {
                return "| DSAEK";
            }
            case TissueTypes.Lenticule: {
                return "| Lenticule";
            }
        }
        return "";
    }, [tissue?.tissueType?.name]);

    const renderLatestStorageSolution = useMemo(() => {
        return latestStorageSolution ? `| ${latestStorageSolution} ` : "";
    }, [latestStorageSolution]);

    const miscInfo = useMemo(() => {
        switch (tissue?.tissueType?.name) {
            case TissueTypes.Sclera:
            case TissueTypes.Sclera1:
            case TissueTypes.Sclera2:
            case TissueTypes.Quarter1:
            case TissueTypes.Quarter2:
            case TissueTypes.Quarter3:
            case TissueTypes.Quarter4:
                return `Sclera | ${renderTerritoryLabel}`;
            case TissueTypes.Cairs:
            case TissueTypes.Cairs1:
            case TissueTypes.CairsCtr:
            case TissueTypes.CairsSeg1:
            case TissueTypes.CairsSeg2:
                return `Stromal Ring | ${tissueSide} | ${renderTerritoryLabel}`;
            case TissueTypes.WholeGlobe:
                return `Whole Eye | ${tissueSide} ${renderLatestStorageSolution} ${tissueTypeName} ${renderTerritoryLabel}`;
            default:
                return `Cornea | ${tissueSide} ${renderLatestStorageSolution} ${tissueTypeName} ${renderTerritoryLabel}`;
        }
    }, [renderLatestStorageSolution, renderTerritoryLabel, tissue?.tissueType?.name, tissueTypeName, tissueSide]);

    return (
        <>
            <div className="d-flex">
                <TextFit style={{ width: "50%" }} className="fw-bold" maxFontSizePx={16} singleLine>
                    {tissueNumWithProcedure}
                </TextFit>
                <TextFit style={{ width: "50%" }} className="fw-bold text-end" maxFontSizePx={16} singleLine>
                    Final Disposition: {tissue?.placement?.tissueUse}
                </TextFit>
            </div>
            <div className="d-flex">
                <TextFit style={{ width: "50%" }} maxFontSizePx={12} singleLine>
                    <span className="me-3">DIN: {getDonorIsbtNumber(donor?.number, referral?.territory)}</span>
                    <span className="me-2">Product Code: {productCode}</span>
                    {showFinP && (
                        <span>FIN (P): {latestProcedure?.office?.territory?.facilityIdentificationNumber}</span>
                    )}
                </TextFit>
                <TextFit style={{ width: "50%", textAlign: "right" }} maxFontSizePx={12} singleLine>
                    {miscInfo}
                </TextFit>
            </div>
            <hr className="my-1" />
        </>
    );
}
